// @ts-nocheck
import React, { FC, useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { PageHeader, Tabs, AddIcon } from '@gamesb42/ui-kit';

import urls from 'constants/urls';
import useCompany from 'hooks/api/useCompany';
import { contractDataBaseType } from 'hooks/api/useContracts';
import { getCompanyById } from 'api/company';
import { Company } from 'types/CompanyTypes';
import CompanyStatusLabel from 'components/CompanyStatus';

import General from './General';
import ActionButton from './ActionButton';
import styles from './styles.module.scss';
import RevenueSources from './RevenueSources';
import Docs from './Docs';
import { AMLKYC } from './AMLKYC';
import baseRequest from 'queries/baseRequest';
import { AppGameInfo } from './AppGameInfo';
import { StatusEnumTitle } from 'components/common/Status';
import AuditLog from './AuditLog';
import { useUserContext } from 'contexts/UserProvider';
import { isAdmin } from 'helpers/token';
import { mapOnboardingData } from 'helpers/mappers/onboardingData';

export interface CompanyTypeParams {
  period_from: string;
  platform_ids: string;
  tab: string;
  date_from: string;
  date_to: string;
  stores_ids: string;
}
interface ViewType {
  requestParams: CompanyTypeParams;
}

const View: FC<ViewType> = ({ requestParams }) => {
  const { t } = useTranslation();
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { companyId, slug } = useParams();
  const [companyData, setCompanyData] = useState<Company>();
  const [companyName, setCompanyName] = useState('');
  const { purchaseClaims, getPdsAccounts, listPdsAccounts } = useCompany();
  const [currentDocument, setCurrentDocument] = useState<contractDataBaseType | null>(null);
  const [isOpen, setIsOpen] = useState();
  const [onboardingData, setOnboardingData] = useState<any>([]);
  const [dontKnowCount, setDontKnow] = useState(0);
  const [status, setStatus] = useState();
  const [faVersion, setFaVersion] = useState(0);
  const { logOut, user } = useUserContext();
  // Убираем локальную переменную isAdmin

  const loadCompany = useCallback(
    () =>
      getCompanyById(companyId)
        .then((data) => {
          setCompanyData(data);
          setCompanyName(data.name || data.legal_name || data.email || '');
        })
        .catch(() => {}),
    [companyId],
  );

  const refreshFA = useCallback(() => setFaVersion((prev) => prev + 1), []);

  const tabsItems = useMemo(() => {
    const result: any = [
      {
        label: t('forms.companies.tabs.general'),
        key: 'general',
        children: <General company={companyData as Company} />,
      },
    ];

    if (companyData?.type === 'client') {
      result.push(
        {
          key: 'general-docs',
          label: t('forms.companies.tabs.docs'),

          children: (
            <Docs
              activeMainTab={slug}
              setCurrentDocument={setCurrentDocument}
              companyId={companyId}
              setIsOpen={setIsOpen}
              setCompanyData={setCompanyData}
              refreshCompany={loadCompany}
              faVersion={faVersion}
            />
          ),
        },
        {
          key: 'revenue-sources',
          label: t('forms.companies.tabs.revenueSources'),
          children: <RevenueSources listPdsAccounts={listPdsAccounts} requestParams={requestParams} />,
        },
        {
          key: 'aml-kyc',
          label: 'AML&KYC',
          children: <AMLKYC companyData={companyData} refreshCompany={loadCompany} onAgreementCreated={refreshFA} />,
        },
        {
          key: 'app-game-info',
          label: `App&Game Info`,
          children: <AppGameInfo rows={onboardingData} getOnboardingData={getOnboardingData} />,
        },
        {
          key: 'audit-log',
          label: `Audit log`,
          children: <AuditLog />,
        },
      );
    }

    return result;
  }, [
    companyData,
    companyId,
    faVersion,
    listPdsAccounts,
    loadCompany,
    onboardingData,
    purchaseClaims,
    refreshFA,
    requestParams,
    slug,
    t,
  ]);

  function updateCompany() {
    setCompanyData(undefined);
    loadCompany();
  }

  function rejectedCompany() {
    companyData!.status = StatusEnumTitle.REJECTED;
  }

  useEffect(() => {
    updateCompany();
    // eslint-disable-next-line no-unused-expressions
    companyId && getPdsAccounts({ params: { company_id: companyId } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // get onboarding data
  function getOnboardingData(): any {
    const sca = window.ENV?.REACT_APP_SCA_URL || process.env.REACT_APP_SCA_URL;

    return baseRequest({
      path: `${sca}/v1/onboarding/${companyId}`,
      options: {
        method: 'GET',
      },
      // у legacy-компаний онбординга может не быть — это не ошибка
      suppressErrorNotificationForStatuses: [404],
    });
  }

  useEffect(() => {
    getOnboardingData()
      .then((data: any) => {
        const result = mapOnboardingData(data);
        setOnboardingData(result.rows);
        setDontKnow(result.dontKnowCount);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (companyData) {
      setStatus(companyData?.status);
    }
  }, [companyData, setCompanyData]);

  if (!companyData) return null;

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={companyName as string}
        back
        onBack={() => history.goBack()}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        // leftContent={<CompanyStatusLabel status={companyData.status} />}
        leftContent={<CompanyStatusLabel status={status} />}
        rightContent={
          isAdmin() && <ActionButton name={companyName as string} updateCompany={updateCompany} status={status} />
        }
      />
      <Tabs
        items={tabsItems.map((tab: any) => {
          const label =
            tab.label === 'App&Game Info' && dontKnowCount > 0 ? (
              <div
                style={{
                  display: 'flex',
                  gap: '4px',
                  alignItems: 'center',
                }}
              >
                <div>{tab.label}</div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '1px 0px 0px',
                    borderRadius: '100px',
                    background: '#EC3E72',
                    width: '15px',
                    height: '16px',
                    color: '#FFF',
                    fontStyle: 'normal',
                    fontWeight: '400',
                    fontSize: '12px',
                    lineHeight: '14px',
                  }}
                >
                  {dontKnowCount}
                </div>
              </div>
            ) : (
              <>{tab.label}</>
            );

          return { label, key: tab.key, children: tab.children };
        })}
        activeKey={slug}
        onChange={(key: string) => {
          if (key) {
            history.push(`${urls.getCompany()}/${key}/${companyId}`);

            return;
          }
          history.push(`${urls.getCompany()}/${companyId}`);
        }}
      />
    </div>
  );
};

export default View;
